import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { deleteTruck } from '../services/truckService';
import apiClient from '../api';
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    Chip,
    Stack,
    Avatar,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Container,
    useTheme,
    useMediaQuery,
    Fab,
    Collapse,
    FormControl,
    InputLabel,
    Select,
    TextField,
    InputAdornment,
    Divider,
    LinearProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import BuildIcon from '@mui/icons-material/Build';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SellIcon from '@mui/icons-material/Sell';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import SpeedIcon from '@mui/icons-material/Speed';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TruckForm from './TruckForm';
import TruckDocumentList from './TruckDocumentList';
import TruckDocSmartUploadDialog from './TruckDocSmartUploadDialog';
import FleetDocumentUploadDialog from './FleetDocumentUploadDialog';
import CloseIcon from '@mui/icons-material/Close';
import TableSkeleton from './skeleton/TableSkeleton';
import { useAuth } from '../context/AuthContext';
import { useVehicleType } from './VehicleTypeSelector';
import { mapSamsaraVehicleType } from '../utils/samsaraVehicleType';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

function TruckList({ onVehicleSelect, samsaraVehicles = [], samsaraConnected = false }) {
    const [trucks, setTrucks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openForm, setOpenForm] = useState(false);
    const [formInitialType, setFormInitialType] = useState('Van');
    const [selectedTruck, setSelectedTruck] = useState(null);
    const [searchTerm, setSearchTerm] = useState(localStorage.getItem('trucks.search') || '');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [statusFilter, setStatusFilter] = useState(localStorage.getItem('trucks.status') || 'all');
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    const [selectedTruckForMenu, setSelectedTruckForMenu] = useState(null);
    const [showDocuments, setShowDocuments] = useState(false);
    const [selectedTruckForDocuments, setSelectedTruckForDocuments] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [truckToDelete, setTruckToDelete] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [docCompliance, setDocCompliance] = useState({});
    const [docsFilter, setDocsFilter] = useState('all'); // all | expired | expiring
    const [smartUploadOpen, setSmartUploadOpen] = useState(false);
    const [fleetUploadOpen, setFleetUploadOpen] = useState(false);
    const [incompleteTrucks, setIncompleteTrucks] = useState([]);
    const [incompleteTotals, setIncompleteTotals] = useState(null);
    const [incompleteOpen, setIncompleteOpen] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

    const { user } = useAuth(); // Get user from context

    const { isAll, isVans, isFlatbeds } = useVehicleType();

    const fetchAbortRef = useRef(null);
    const fetchDocCompliance = useCallback(async () => {
        try {
            const res = await apiClient.get('/truck-documents/compliance-summary');
            setDocCompliance(res.data?.data || {});
        } catch (err) {
            console.warn('[TruckList] compliance summary failed', err?.message || err);
        }
    }, []);

    const fetchIncompleteTrucks = useCallback(async () => {
        if (!user || (user.role !== 'admin' && user.role !== 'company_admin')) {
            setIncompleteTrucks([]);
            setIncompleteTotals(null);
            return;
        }
        try {
            const res = await apiClient.get('/trucks/incomplete');
            setIncompleteTrucks(res.data?.data || []);
            setIncompleteTotals(res.data?.totals || null);
        } catch (err) {
            console.warn('[TruckList] incomplete trucks failed', err?.message || err);
        }
    }, [user]);

    const fetchTrucks = useCallback(async (signal) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get('/trucks', { signal });
            const all = response.data;
            setTrucks(all);
            fetchDocCompliance();
            fetchIncompleteTrucks();
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            setError('Failed to fetch trucks. Please try again later.');
            console.error('Fetch error:', err);
        } finally {
            setLoading(false);
        }
    }, [fetchDocCompliance, fetchIncompleteTrucks]);

    useEffect(() => {
        const controller = new AbortController();
        fetchAbortRef.current = controller;
        fetchTrucks(controller.signal);
        return () => {
            try { controller.abort(); } catch (_) {}
        };
    }, [fetchTrucks]);

    // Debounce searchTerm to reduce re-filtering
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearchTerm(searchTerm), 250);
        return () => clearTimeout(id);
    }, [searchTerm]);

    // Memoize the merged trucks calculation to prevent unnecessary re-processing
    const mergedTrucks = useMemo(() => {
        let rows;
        if (samsaraConnected && samsaraVehicles.length > 0) {
            rows = samsaraVehicles.map(samsaraVehicle => {
                // Find matching local truck data if it exists
                const localTruck = trucks.find(truck => 
                    truck.unitNumber === samsaraVehicle.name || 
                    truck.vin === samsaraVehicle.vin ||
                    (truck.samsaraId && String(truck.samsaraId) === String(samsaraVehicle.id))
                );
                
                // Determine status - /vehicles endpoint doesn't have engineState
                // Default to Active unless we have location data with engineState
                let vehicleStatus = 'Active';
                if (samsaraVehicle.engineState) {
                    vehicleStatus = samsaraVehicle.engineState === 'ON' ? 'Active' : 
                                  samsaraVehicle.engineState === 'OFF' ? 'Inactive' : 'Maintenance';
                } else if (samsaraVehicle.status) {
                    vehicleStatus = samsaraVehicle.status;
                } else if (localTruck?.status) {
                    vehicleStatus = localTruck.status;
                }

                const samsaraData = {
                    id: String(samsaraVehicle.id),
                    name: samsaraVehicle.name,
                    location: samsaraVehicle.location,
                    engineState: samsaraVehicle.engineState,
                    lastSeen: samsaraVehicle.lastSeen,
                    realTime: !!samsaraVehicle.location
                };

                // Prefer Mongo _id for documents/CRUD; keep Samsara id only in samsaraData
                if (localTruck) {
                    return {
                        ...localTruck,
                        unitNumber: localTruck.unitNumber || samsaraVehicle.name || `Vehicle ${samsaraVehicle.id}`,
                        vin: localTruck.vin || samsaraVehicle.vin || 'N/A',
                        make: localTruck.make || samsaraVehicle.make || 'Unknown',
                        model: localTruck.model || samsaraVehicle.model || 'Unknown',
                        year: localTruck.year || samsaraVehicle.year || 'N/A',
                        type: localTruck.type || mapSamsaraVehicleType(samsaraVehicle),
                        status: vehicleStatus,
                        samsaraId: localTruck.samsaraId || String(samsaraVehicle.id),
                        samsaraData,
                        isSamsaraOnly: false,
                        rowKey: `${localTruck._id}-${samsaraVehicle.id}`,
                    };
                }

                return {
                    _id: `samsara-${samsaraVehicle.id}`,
                    unitNumber: samsaraVehicle.name || `Vehicle ${samsaraVehicle.id}`,
                    vin: samsaraVehicle.vin || 'N/A',
                    make: samsaraVehicle.make || 'Unknown',
                    model: samsaraVehicle.model || 'Unknown',
                    year: samsaraVehicle.year || 'N/A',
                    type: mapSamsaraVehicleType(samsaraVehicle),
                    status: vehicleStatus,
                    samsaraId: String(samsaraVehicle.id),
                    samsaraData,
                    isSamsaraOnly: true,
                    rowKey: `samsara-${samsaraVehicle.id}`,
                };
            });
        } else {
            rows = trucks.map(truck => {
                // For local trucks, try to find matching Samsara data
                if (samsaraConnected && samsaraVehicles.length > 0) {
                    const samsaraVehicle = samsaraVehicles.find(sv => 
                        sv.name === truck.unitNumber || 
                        sv.vin === truck.vin ||
                        String(sv.id) === String(truck.samsaraId)
                    );
                    
                    if (samsaraVehicle) {
                        return {
                            ...truck,
                            isSamsaraOnly: false,
                            samsaraData: {
                                id: String(samsaraVehicle.id),
                                name: samsaraVehicle.name,
                                location: samsaraVehicle.location,
                                engineState: samsaraVehicle.engineState,
                                lastSeen: samsaraVehicle.lastSeen,
                                realTime: true
                            },
                            status: samsaraVehicle.engineState ? 
                                   (samsaraVehicle.engineState === 'ON' ? 'Active' : 
                                    samsaraVehicle.engineState === 'OFF' ? 'Inactive' : 'Maintenance') :
                                   (samsaraVehicle.status || truck.status || 'Active'),
                            rowKey: String(truck._id),
                        };
                    }
                }
                
                return { ...truck, isSamsaraOnly: false, rowKey: String(truck._id) };
            });
        }

        // Deduplicate: multiple Samsara vehicles can match one Mongo truck → same _id
        const seen = new Set();
        return rows.filter((row) => {
            const id = String(row._id);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }, [samsaraConnected, samsaraVehicles, trucks]);

    // Picker list for Smart Upload: include live Samsara unit numbers even when Mongo
    // still has the VIN on an "old 22" twin (merged list would otherwise hide #22).
    const smartUploadTrucks = useMemo(() => {
        const isRetired = (unit) => /\b(old|deactivated|retired|inactive|previously\s+paired|do\s*not\s*use)\b/i.test(String(unit || ''))
            || /^old[\s_-]*\d/i.test(String(unit || ''))
            || /^vehicle\s+\d+/i.test(String(unit || '').trim());
        const map = new Map();
        for (const t of trucks || []) {
            if (!t?._id || isRetired(t.unitNumber)) continue;
            map.set(String(t._id), t);
        }
        if (samsaraConnected && samsaraVehicles?.length) {
            for (const sv of samsaraVehicles) {
                const name = String(sv.name || '').trim();
                if (!name || isRetired(name)) continue;
                const sid = String(sv.id);
                const localByUnit = (trucks || []).find(
                    (t) => String(t.unitNumber || '').trim() === name && !isRetired(t.unitNumber)
                );
                if (localByUnit?._id) {
                    map.set(String(localByUnit._id), {
                        ...localByUnit,
                        vin: localByUnit.vin || sv.vin,
                        make: localByUnit.make || sv.make,
                        samsaraId: localByUnit.samsaraId || sid,
                    });
                    continue;
                }
                // Prefer a selectable Samsara row over a retired Mongo twin that owns the VIN
                map.set(`samsara-${sid}`, {
                    _id: `samsara-${sid}`,
                    unitNumber: name,
                    vin: sv.vin || undefined,
                    make: sv.make || '',
                    model: sv.model || '',
                    year: sv.year,
                    samsaraId: sid,
                    isSamsaraOnly: true,
                });
            }
        }
        return [...map.values()].sort((a, b) =>
            String(a.unitNumber).localeCompare(String(b.unitNumber), undefined, { numeric: true })
        );
    }, [trucks, samsaraVehicles, samsaraConnected]);

    // Derived filtered trucks (memoized)
    const filteredTrucks = useMemo(() => {
        let filtered = mergedTrucks.filter(truck => {
            if (isAll) return true;
            const t = (truck.type || '').toLowerCase();
            if (isVans) return t === 'van' || t === 'vans';
            if (isFlatbeds) return t === 'flatbed' || t === 'flatbeds';
            return true;
        });

        if (statusFilter !== 'all') {
            filtered = filtered.filter(truck => truck.status === statusFilter);
        }

        if (debouncedSearchTerm.trim()) {
            const lowercasedSearch = debouncedSearchTerm.toLowerCase();
            filtered = filtered.filter(truck =>
                truck.unitNumber?.toString().toLowerCase().includes(lowercasedSearch) ||
                truck.vin?.toLowerCase().includes(lowercasedSearch) ||
                truck.make?.toLowerCase().includes(lowercasedSearch) ||
                truck.model?.toLowerCase().includes(lowercasedSearch) ||
                truck.year?.toString().includes(lowercasedSearch)
            );
        }

        if (docsFilter === 'expired') {
            filtered = filtered.filter((truck) => (docCompliance[String(truck._id)]?.expired || 0) > 0);
        } else if (docsFilter === 'expiring') {
            filtered = filtered.filter((truck) => {
                const c = docCompliance[String(truck._id)];
                return (c?.expired || 0) > 0 || (c?.expiringSoon || 0) > 0;
            });
        }

        return filtered;
    }, [mergedTrucks, statusFilter, debouncedSearchTerm, isAll, isVans, isFlatbeds, docsFilter, docCompliance]);

    // Open delete confirmation dialog
    const handleDeleteClick = (truck) => {
        setTruckToDelete(truck);
        setDeleteConfirmOpen(true);
    };

    // Handle the actual deletion after confirmation
    const handleDeleteConfirm = async () => {
        if (!truckToDelete) return;
        const deleteId = truckToDelete.isSamsaraOnly ? null : truckToDelete._id;
        if (!deleteId || String(deleteId).startsWith('samsara-')) {
            setError('Create a local truck record before deleting, or remove the vehicle in Samsara.');
            setDeleteConfirmOpen(false);
            setTruckToDelete(null);
            return;
        }
        
        try {
            await deleteTruck(deleteId);
            setTrucks(trucks.filter(truck => truck._id !== deleteId));
            setDeleteConfirmOpen(false);
            setTruckToDelete(null);
        } catch (err) {
            setError('Failed to delete truck.');
            console.error(err);
        }
    };

    const handleViewRelated = async (truck) => {
        try {
            let resolved = truck;
            if (truck?.isSamsaraOnly || String(truck?._id || '').startsWith('samsara-')) {
                const res = await apiClient.post('/trucks/ensure-from-samsara', {
                    samsaraId: truck.samsaraId || truck.samsaraData?.id,
                    unitNumber: truck.unitNumber,
                    vin: truck.vin && truck.vin !== 'N/A' ? truck.vin : undefined,
                    make: truck.make !== 'Unknown' ? truck.make : undefined,
                    model: truck.model !== 'Unknown' ? truck.model : undefined,
                    year: truck.year !== 'N/A' ? truck.year : undefined,
                    type: truck.type,
                });
                const created = res.data?.data;
                if (!created?._id) {
                    setError(res.data?.message || 'Could not create a local truck record for documents.');
                    return;
                }
                resolved = {
                    ...truck,
                    ...created,
                    _id: created._id,
                    isSamsaraOnly: false,
                    samsaraData: truck.samsaraData,
                };
                setTrucks((prev) => {
                    const exists = prev.some((t) => String(t._id) === String(created._id));
                    return exists ? prev.map((t) => (String(t._id) === String(created._id) ? { ...t, ...created } : t)) : [...prev, created];
                });
            }
            setSelectedTruckForDocuments(resolved);
            setShowDocuments(true);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.message || 'Could not open documents for this truck.';
            setError(msg);
            console.error('[TruckList] open documents failed', err);
        }
    };

    // Cancel delete operation
    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setTruckToDelete(null);
    };

    const handleAdd = () => {
        setSelectedTruck(null);
        setFormInitialType(null);
        setOpenForm(true);
    };

    const handleEdit = (truck) => {
        setSelectedTruck(truck);
        setOpenForm(true);
    };

    const handleCloseForm = () => {
        setOpenForm(false);
        setSelectedTruck(null);
    };

    const handleSave = () => {
        const controller = new AbortController();
        try { fetchTrucks(controller.signal); } finally {
            // let the previous request finish; caller can decide to abort
        }
    };

    // Handle search term change
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Handle status filter change
    const handleStatusFilterChange = (status) => {
        setStatusFilter(status);
    };

    // Persist basic filters
    useEffect(() => {
        localStorage.setItem('trucks.search', searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        localStorage.setItem('trucks.status', statusFilter);
    }, [statusFilter]);

    // Handle menu open
    const handleMenuOpen = (event, truck) => {
        event.stopPropagation();
        setActionMenuAnchor(event.currentTarget);
        setSelectedTruckForMenu(truck);
    };

    // Handle menu close
    const handleMenuClose = () => {
        setActionMenuAnchor(null);
        setSelectedTruckForMenu(null);
    };

    // Map status to color - can be refined or moved to utils
    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return 'success';
            case 'Maintenance': return 'warning';
            case 'Inactive': return 'error';
            case 'Sold': return 'default';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'Active': return <CheckCircleIcon />;
            case 'Maintenance': return <BuildIcon />;
            case 'Inactive': return <WarningIcon />;
            case 'Sold': return <SellIcon />;
            default: return <DirectionsCarIcon />;
        }
    };

    const getUnitNoun = (t) => (t?.type === 'Trailer' ? 'Trailer' : 'Truck');

    // Calculate truck statistics
    const truckStats = trucks.reduce((acc, truck) => {
        const status = truck.status || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    const totalTrucks = trucks.length;
    const activeTrucks = truckStats['Active'] || 0;
    const maintenanceTrucks = truckStats['Maintenance'] || 0;
    const inactiveTrucks = truckStats['Inactive'] || 0;
    const soldTrucks = truckStats['Sold'] || 0;
    const fleetHealthPercentage = totalTrucks > 0 ? Math.round((activeTrucks / totalTrucks) * 100) : 0;

    // Show skeleton while loading
    if (loading) {
        return (
            <Container maxWidth="xl" disableGutters sx={{ py: 3 }}>
                <TableSkeleton rows={8} showHeader={true} showActions={true} />
            </Container>
        );
    }


    const renderTableView = () => (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderLeft: '3px solid', borderLeftColor: 'primary.main', borderRadius: 2 }}>
            <TableContainer sx={{ maxHeight: 560 }}>
                <Table stickyHeader aria-label="truck fleet table">
                    <TableHead sx={{ bgcolor: 'rgba(233, 79, 55, 0.08)' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Unit
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Vehicle
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                Status
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">
                                Mileage
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                                VIN
                            </TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="center">
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredTrucks.map((truck, index) => (
                            <TableRow 
                                hover 
                                key={truck.rowKey || `${truck._id}-${index}`}
                                sx={{ 
                                    '&:last-child td, &:last-child th': { border: 0 },
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    // Select for Vehicle Details only — documents open via Documents button
                                    if (onVehicleSelect) {
                                        onVehicleSelect({
                                            id: truck.samsaraData?.id || truck._id,
                                            name: truck.samsaraData?.name || truck.unitNumber,
                                            vin: truck.vin,
                                            make: truck.make,
                                            model: truck.model,
                                            year: truck.year,
                                            status: truck.status,
                                            type: truck.type,
                                            engineState: truck.samsaraData?.engineState,
                                            location: truck.samsaraData?.location,
                                            lastSeen: truck.samsaraData?.lastSeen
                                        });
                                    }
                                }}
                            >
                                <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Avatar 
                                            sx={{ 
                                                width: 40, 
                                                height: 40,
                                                bgcolor: getStatusColor(truck.status) === 'success' ? 'success.main' : 
                                                       getStatusColor(truck.status) === 'warning' ? 'warning.main' : 
                                                       getStatusColor(truck.status) === 'error' ? 'error.main' : 'grey.500'
                                            }}
                                        >
                                            <LocalShippingIcon fontSize="small" />
                                        </Avatar>
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                    #{truck.unitNumber}
                                                </Typography>
                                                {truck.samsaraData?.realTime && (
                                                    <Chip 
                                                        label="Live" 
                                                        size="small" 
                                                        color="success" 
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.7rem', height: 20 }}
                                                    />
                                                )}
                                                {(() => {
                                                    const c = docCompliance[String(truck._id)];
                                                    if (!c) return null;
                                                    if (c.expired > 0) {
                                                        return (
                                                            <Chip
                                                                label={`${c.expired} expired`}
                                                                size="small"
                                                                color="error"
                                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                                            />
                                                        );
                                                    }
                                                    if (c.expiringSoon > 0) {
                                                        return (
                                                            <Chip
                                                                label={`${c.expiringSoon} expiring`}
                                                                size="small"
                                                                color="warning"
                                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                                            />
                                                        );
                                                    }
                                                    if (c.count > 0) {
                                                        return (
                                                            <Chip
                                                                label={`${c.count} docs`}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                                            />
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </Box>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {truck.year} {truck.make}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {truck.model}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={truck.type || 'Van'}
                                        size="small"
                                        variant="outlined"
                                        color="default"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={truck.status}
                                        color={getStatusColor(truck.status)}
                                        size="small"
                                        icon={getStatusIcon(truck.status)}
                                        variant="filled"
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {truck.mileage?.toLocaleString() || '0'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        miles
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontFamily: 'monospace',
                                            fontSize: '0.875rem',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        {truck.vin ? `...${truck.vin.slice(-6)}` : 'N/A'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                        <Tooltip title="View Documents">
                                            <IconButton 
                                                size="small" 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewRelated(truck);
                                                }}
                                            >
                                                <DocumentScannerIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {user?.role !== 'company_user' && (
                                            <>
                                                <Tooltip title={`Edit ${getUnitNoun(truck)}`}>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEdit(truck);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={`Delete ${getUnitNoun(truck)}`}>
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteClick(truck);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {filteredTrucks.length === 0 && (
                <Box sx={{ p: 6, textAlign: 'center' }}>
                    <LocalShippingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No trucks found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {trucks.length === 0 
                            ? user?.role === 'company_user'
                                ? "No trucks available in your fleet yet."
                                : "Start by adding your first truck to your fleet."
                            : "Try adjusting your search or filter criteria."
                        }
                    </Typography>
                </Box>
            )}
        </Paper>
    );

    const renderMobileList = () => (
        <Stack spacing={1}>
            {filteredTrucks.map((truck, index) => {
                const compliance = docCompliance[String(truck._id)];
                const vehicle = [truck.year, truck.make, truck.model].filter(Boolean).join(' ');
                let docLabel = 'No documents';
                let docColor = 'default';
                if (compliance?.expired > 0) {
                    docLabel = `${compliance.expired} expired`;
                    docColor = 'error';
                } else if (compliance?.expiringSoon > 0) {
                    docLabel = `${compliance.expiringSoon} expiring`;
                    docColor = 'warning';
                } else if (compliance?.count > 0) {
                    docLabel = `${compliance.count} documents`;
                    docColor = 'success';
                }
                return (
                    <Paper
                        key={truck.rowKey || `${truck._id}-${index}`}
                        variant="outlined"
                        onClick={() => handleViewRelated(truck)}
                        sx={{
                            px: 1.5,
                            py: 1.25,
                            cursor: 'pointer',
                            borderLeft: '4px solid',
                            borderLeftColor: docColor === 'error' ? 'error.main' : docColor === 'warning' ? 'warning.main' : '#E94F37',
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', lineHeight: 1.1 }}>
                                    #{truck.unitNumber}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap>
                                    {vehicle || truck.type || 'Truck'}
                                </Typography>
                                <Chip
                                    label={docLabel}
                                    size="small"
                                    color={docColor === 'default' ? 'default' : docColor}
                                    variant={docColor === 'default' ? 'outlined' : 'filled'}
                                    sx={{ mt: 0.75, fontWeight: 700 }}
                                />
                            </Box>
                            <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                                <Button
                                    variant="contained"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        handleViewRelated(truck);
                                    }}
                                    startIcon={<DocumentScannerIcon />}
                                    sx={{ fontWeight: 800, px: 1.5 }}
                                >
                                    Documents
                                </Button>
                                {user?.role !== 'company_user' && (
                                    <Stack direction="row" spacing={0.5}>
                                        <IconButton
                                            size="small"
                                            aria-label={`Edit ${getUnitNoun(truck)}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleEdit(truck);
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            aria-label={`Delete ${getUnitNoun(truck)}`}
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleDeleteClick(truck);
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                    </Paper>
                );
            })}
            {filteredTrucks.length === 0 && (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <LocalShippingIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">No trucks found</Typography>
                </Box>
            )}
        </Stack>
    );

    const renderCardView = () => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {filteredTrucks.map((truck, index) => (
                <Box 
                    key={truck.rowKey || `${truck._id}-${index}`}
                    sx={{ 
                        flex: 1,
                        minWidth: { xs: '100%', sm: 'calc(50% - 15px)', lg: 'calc(33.33% - 20px)' },
                        borderRadius: 3,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'pointer',
                        '&:hover': { 
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8]
                        },
                        position: 'relative',
                        border: `2px solid ${getStatusColor(truck.status) === 'success' ? theme.palette.success.light :
                                                getStatusColor(truck.status) === 'warning' ? theme.palette.warning.light :
                                                getStatusColor(truck.status) === 'error' ? theme.palette.error.light : 'transparent'}`
                    }}
                    onClick={() => handleViewRelated(truck)}
                >
                    {/* Status Badge */}
                    <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
                        <Chip 
                            label={truck.status}
                            color={getStatusColor(truck.status)}
                            size="small"
                            icon={getStatusIcon(truck.status)}
                            sx={{ fontWeight: 600 }}
                        />
                    </Box>

                    <Box sx={{ p: 3, pb: 1 }}>
                        <Stack spacing={2}>
                            {/* Header */}
                            <Stack direction="row" spacing={2} alignItems="flex-start">
                                <Avatar 
                                    sx={{ 
                                        bgcolor: getStatusColor(truck.status) === 'success' ? 'success.main' : 
                                               getStatusColor(truck.status) === 'warning' ? 'warning.main' : 
                                               getStatusColor(truck.status) === 'error' ? 'error.main' : 'grey.500',
                                        width: 56,
                                        height: 56
                                    }}
                                >
                                    <LocalShippingIcon sx={{ fontSize: 28 }} />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography 
                                        variant="h6" 
                                        fontWeight={600}
                                        noWrap
                                        sx={{ pr: 8 }} // Make room for the badge
                                    >
                                        Unit #{truck.unitNumber}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        VIN: {truck.vin?.slice(-8) || 'Not provided'}
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Vehicle Info */}
                            <Box>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle1" fontWeight={500}>
                                        {truck.year} {truck.make} {truck.model}
                                    </Typography>
                                    <Chip 
                                        label={truck.type || 'Van'}
                                        size="small"
                                        variant="outlined"
                                        sx={{ 
                                            bgcolor: truck.type === 'Flatbed' ? 'warning.light' : 'info.light',
                                            borderColor: truck.type === 'Flatbed' ? 'warning.main' : 'info.main',
                                            color: truck.type === 'Flatbed' ? 'warning.dark' : 'info.dark'
                                        }}
                                    />
                                </Stack>
                                <Stack spacing={1}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <SpeedIcon fontSize="small" color="action" />
                                        <Typography variant="body2">
                                            {truck.mileage?.toLocaleString() || 'N/A'} miles
                                        </Typography>
                                    </Stack>
                                    {truck.engineModel && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <BuildIcon fontSize="small" color="action" />
                                            <Typography variant="body2">
                                                {truck.engineModel}
                                            </Typography>
                                        </Stack>
                                    )}
                                    {truck.acquiredDate && (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <DateRangeIcon fontSize="small" color="action" />
                                            <Typography variant="body2">
                                                Acquired: {new Date(truck.acquiredDate).toLocaleDateString()}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            </Box>

                            {/* Status Progress for Active trucks */}
                            {truck.status === 'Active' && truck.mileage && (
                                <Box>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="caption" color="text.secondary">
                                            Fleet Performance
                                        </Typography>
                                        <Typography variant="caption" fontWeight={600}>
                                            Excellent
                                        </Typography>
                                    </Stack>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={85} 
                                        sx={{ height: 6, borderRadius: 3 }}
                                        color="success"
                                    />
                                </Box>
                            )}
                        </Stack>
                    </Box>

                    <Box sx={{ px: 3, py: 2, pt: 0 }}>
                        <Button 
                            size="small" 
                            startIcon={<DocumentScannerIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleViewRelated(truck);
                            }}
                        >
                            Documents
                        </Button>
                        {user?.role !== 'company_user' && (
                            <>
                                <Button 
                                    size="small" 
                                    startIcon={<EditIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEdit(truck);
                                    }}
                                >
                                    Edit
                                </Button>
                                <IconButton 
                                    size="small" 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleMenuOpen(e, truck);
                                    }}
                                    sx={{ ml: 'auto' }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            </>
                        )}
                    </Box>
                </Box>
            ))}
            {filteredTrucks.length === 0 && (
                <Box sx={{ flex: 1, p: 4, textAlign: 'center', borderRadius: 3 }}>
                    <LocalShippingIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No trucks found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {filteredTrucks.length === 0 && trucks.length > 0
                            ? "Try adjusting your search or filter criteria."
                            : "Start by adding your first truck to your fleet."
                        }
                    </Typography>
                    {user?.role !== 'company_user' && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAdd}
                        >
                            Add First Truck
                        </Button>
                    )}
                </Box>
            )}
        </Box>
    );

    return (
        <Container maxWidth="xl" disableGutters sx={{ pt: 0, pb: { xs: 0.5, md: 1 } }}>

            {error && (
                <Alert 
                    severity="error" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {incompleteTrucks.length > 0 && (user?.role === 'admin' || user?.role === 'company_admin') && (
                <Alert
                    severity="warning"
                    sx={{ mb: 2, borderRadius: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => setIncompleteOpen(true)}>
                            Review
                        </Button>
                    }
                >
                    {incompleteTrucks.length} truck{incompleteTrucks.length === 1 ? '' : 's'} missing VIN/make/model/year
                    {incompleteTotals?.unlinkedSamsara
                        ? ` or not linked to Samsara (${incompleteTotals.unlinkedSamsara} unlinked)`
                        : ''}
                    . Completing these helps prevent checklist submit failures.
                </Alert>
            )}


            {/* Controls */}
            <Paper sx={{ p: 1, mb: 1.5, borderRadius: 2, borderTop: '3px solid', borderTopColor: 'primary.main' }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}
                    mb={showFilters ? 2 : 0}
                >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Fleet Overview ({filteredTrucks.length})
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                        {user?.role !== 'company_user' && (
                            <>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAdd}
                                >
                                    Add Unit
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AutoFixHighIcon />}
                                    onClick={() => setSmartUploadOpen(true)}
                                >
                                    Smart Upload
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<CloudUploadIcon />}
                                    onClick={() => setFleetUploadOpen(true)}
                                >
                                    Upload to all trucks
                                </Button>
                            </>
                        )}
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                const rows = filteredTrucks.map(t => ({
                                    Unit: t.unitNumber,
                                    Type: t.type || '',
                                    Status: t.status || '',
                                    Year: t.year || '',
                                    Make: t.make || '',
                                    Model: t.model || '',
                                    VIN: t.vin || '',
                                    Mileage: t.mileage || ''
                                }));
                                const headers = Object.keys(rows[0] || { Unit: '', Type: '', Status: '', Year: '', Make: '', Model: '', VIN: '', Mileage: '' });
                                const csv = [headers.join(','), ...rows.map(r => headers.map(h => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
                                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                                const url = URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', 'trucks.csv');
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                URL.revokeObjectURL(url);
                            }}
                        >
                            Export CSV
                        </Button>
                        <Button
                            size="small"
                            startIcon={<FilterListIcon />}
                            onClick={() => setShowFilters(!showFilters)}
                            variant={showFilters ? "contained" : "outlined"}
                        >
                            Filters
                        </Button>
                    </Stack>
                </Stack>

                <Collapse in={showFilters}>
                    <Stack spacing={2} sx={{ mb: 2 }}>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={2}
                        >
                            <TextField
                                size="small"
                                placeholder="Search trucks..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                }}
                                sx={{ flex: 1 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Filter by Status</InputLabel>
                                <Select
                                    value={statusFilter}
                                    label="Filter by Status"
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All Trucks</MenuItem>
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Maintenance">Maintenance</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                    <MenuItem value="Sold">Sold</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 220 }}>
                                <InputLabel>Document status</InputLabel>
                                <Select
                                    value={docsFilter}
                                    label="Document status"
                                    onChange={(e) => setDocsFilter(e.target.value)}
                                >
                                    <MenuItem value="all">All documents</MenuItem>
                                    <MenuItem value="expiring">Expired or expiring</MenuItem>
                                    <MenuItem value="expired">Has expired docs</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        {/* Quick Status Filter Buttons */}
                        <Stack 
                            direction="row" 
                            spacing={1} 
                            sx={{ flexWrap: 'wrap', gap: 1 }}
                        >
                            <Chip 
                                label={`All (${totalTrucks})`}
                                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                                color={statusFilter === 'all' ? 'primary' : 'default'}
                                onClick={() => handleStatusFilterChange('all')}
                                clickable
                            />
                            <Chip 
                                label={`Active (${activeTrucks})`}
                                variant={statusFilter === 'Active' ? 'filled' : 'outlined'}
                                color={statusFilter === 'Active' ? 'success' : 'default'}
                                onClick={() => handleStatusFilterChange('Active')}
                                clickable
                                icon={<CheckCircleIcon />}
                            />
                            <Chip 
                                label={`Maintenance (${maintenanceTrucks})`}
                                variant={statusFilter === 'Maintenance' ? 'filled' : 'outlined'}
                                color={statusFilter === 'Maintenance' ? 'warning' : 'default'}
                                onClick={() => handleStatusFilterChange('Maintenance')}
                                clickable
                                icon={<BuildIcon />}
                            />
                            <Chip 
                                label={`Inactive (${inactiveTrucks})`}
                                variant={statusFilter === 'Inactive' ? 'filled' : 'outlined'}
                                color={statusFilter === 'Inactive' ? 'error' : 'default'}
                                onClick={() => handleStatusFilterChange('Inactive')}
                                clickable
                                icon={<WarningIcon />}
                            />
                            {soldTrucks > 0 && (
                                <Chip 
                                    label={`Sold (${soldTrucks})`}
                                    variant={statusFilter === 'Sold' ? 'filled' : 'outlined'}
                                    color={statusFilter === 'Sold' ? 'default' : 'default'}
                                    onClick={() => handleStatusFilterChange('Sold')}
                                    clickable
                                    icon={<SellIcon />}
                                />
                            )}
                        </Stack>
                    </Stack>
                </Collapse>
            </Paper>

            {/* Content */}
            <Box sx={{ mb: 3 }}>
                {isMobile ? renderMobileList() : renderTableView()}
            </Box>

            {/* Mobile FABs */}
            {isMobile && user?.role !== 'company_user' && (
                <>
                    <Fab
                        color="primary"
                        sx={{
                            position: 'fixed',
                            bottom: 16,
                            right: 16,
                            zIndex: 1000
                        }}
                        onClick={handleAdd}
                        aria-label="Add Truck"
                    >
                        <AddIcon />
                    </Fab>
                    
                </>
            )}

            {/* Action Menu */}
            <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 150 } }}
            >
                <MenuItem onClick={() => {
                    if (selectedTruckForMenu) handleViewRelated(selectedTruckForMenu);
                    handleMenuClose();
                }}>
                    <ListItemIcon>
                        <DocumentScannerIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View Documents</ListItemText>
                </MenuItem>
                {user?.role !== 'company_user' && (
                    <>
                        <MenuItem onClick={() => {
                            if (selectedTruckForMenu) handleEdit(selectedTruckForMenu);
                            handleMenuClose();
                        }}>
                            <ListItemIcon>
                                <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>{`Edit ${getUnitNoun(selectedTruckForMenu)}`}</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => {
                            if (selectedTruckForMenu) handleDeleteClick(selectedTruckForMenu);
                            handleMenuClose();
                        }} sx={{ color: 'error.main' }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText>{`Delete ${getUnitNoun(selectedTruckForMenu)}`}</ListItemText>
                        </MenuItem>
                    </>
                )}
            </Menu>

            {/* Truck Form Dialog */}
            <TruckForm
                open={openForm}
                onClose={handleCloseForm}
                truck={selectedTruck}
                onSave={handleSave}
                initialType={formInitialType}
                samsaraConnected={samsaraConnected}
            />

            {/* Documents Dialog */}
            <Dialog
                open={showDocuments}
                onClose={() => {
                    setShowDocuments(false);
                    fetchDocCompliance();
                }}
                fullWidth
                maxWidth="lg"
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">
                                Documents for Unit #{selectedTruckForDocuments?.unitNumber}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedTruckForDocuments?.year} {selectedTruckForDocuments?.make} {selectedTruckForDocuments?.model}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setShowDocuments(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    {selectedTruckForDocuments && (
                        <TruckDocumentList
                            truckId={selectedTruckForDocuments._id}
                            truck={selectedTruckForDocuments}
                        />
                    )}
                </DialogContent>
            </Dialog>

            <Dialog
                open={incompleteOpen}
                onClose={() => setIncompleteOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" component="span">Incomplete truck records</Typography>
                    <IconButton onClick={() => setIncompleteOpen(false)} size="small" aria-label="Close">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        These units are missing details that can block weekly checklist submit or create duplicate Samsara rows. Edit the truck in Truckbook to fill VIN / make / model / year, or open a Samsara-linked unit so it gets linked.
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Unit</TableCell>
                                    <TableCell>Vehicle</TableCell>
                                    <TableCell>Issues</TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {incompleteTrucks.map((t) => (
                                    <TableRow key={String(t._id)} hover>
                                        <TableCell>#{t.unitNumber}</TableCell>
                                        <TableCell>
                                            {[t.year, t.make, t.model].filter(Boolean).join(' ') || '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                {(t.issues || []).map((issue) => (
                                                    <Chip
                                                        key={issue}
                                                        size="small"
                                                        label={
                                                            ({
                                                                missing_or_invalid_vin: 'VIN',
                                                                missing_make: 'Make',
                                                                missing_model: 'Model',
                                                                missing_year: 'Year',
                                                                unlinked_samsara: 'No Samsara link',
                                                            })[issue] || issue
                                                        }
                                                        color={issue === 'unlinked_samsara' ? 'default' : 'warning'}
                                                        sx={{ mb: 0.5 }}
                                                    />
                                                ))}
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    const full = trucks.find((x) => String(x._id) === String(t._id));
                                                    if (full) {
                                                        setSelectedTruck(full);
                                                        setOpenForm(true);
                                                        setIncompleteOpen(false);
                                                    }
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIncompleteOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <TruckDocSmartUploadDialog
                open={smartUploadOpen}
                onClose={() => setSmartUploadOpen(false)}
                trucks={smartUploadTrucks}
                onFiled={() => {
                    fetchDocCompliance();
                }}
            />

            <FleetDocumentUploadDialog
                open={fleetUploadOpen}
                onClose={() => setFleetUploadOpen(false)}
                truckCount={smartUploadTrucks.length || trucks.length}
                onComplete={() => {
                    fetchDocCompliance();
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="error" />
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>Unit #{truckToDelete?.unitNumber}</strong>?
                        This action cannot be undone and will also remove all associated documents.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 0 }}>
                    <Button onClick={handleDeleteCancel}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        startIcon={<DeleteIcon />}
                    >
                        {`Delete ${getUnitNoun(truckToDelete)}`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default TruckList; 